import React, { useState, useEffect } from 'react';
import { useMetaData } from '../../../contexts/metadata';
import {
  useGetActiveGroupName,
  useTogglePatientGroupForm
} from '../../../libs/group';
import {
  UrlStateActionType,
  useUrlStateUpdater
} from '../../../contexts/active-dialogs/url';
import { CardBlock, CardFrame } from '../../organisms/card';
import Toast from '../../organisms/toast';
import Callout from '../../organisms/callout';
import Modal from '../../organisms/modal';
import Pagination from '../../organisms/pagination';
import PatientGroupControl from '../../organisms/patient-groups/control';
import PatientGroupForm from '../../organisms/patient-groups/form';
import PushMessageFilter from '../../organisms/pm/filter';
import PMTemplateCard from '../../organisms/pm/card';
import { BulkPMPayload } from '../../organisms/pm/bulkPmDialog';

import { Col, Container, Row } from '../../../grid';
import { Label } from '@blueprintjs/core';
import Button from '../../molecules/button';
import Icon from '../../atoms/icon';
import AutoText from '../../atoms/text/auto';
import { faCaretDown, faShare } from '@fortawesome/pro-solid-svg-icons';
import {
  IPMTemplate,
  SendBulkPMResponse,
  useGetPMTemplates
} from '../../../libs/pm';
import { PostJSON } from '../../../libs/request';
import { usePatientGroup } from '../../../contexts/patient-group';
import { useApps } from '../../../contexts/apps';
import { useTriage } from '../../../contexts/triage';
import { usePinnedTemplates } from '../../../libs/hooks/usePinnedTemplates';

const TEMPLATE_PAGE_LIMIT = 5;

const NewBulkPM = ({ header, triageCategory }: BulkPMPayload): JSX.Element => {
  const { terms } = useMetaData();
  const { data } = useTriage();
  const { apps } = useApps().data || {};
  const [isPGSelectExpanded, setIsPGSelectExpanded] = useState(false);
  const triagePatientIds = data?.patientIdsPerCategory[triageCategory];
  const patientView = header?.replace(/\(.*?\)/g, '').trim();
  const patientCount = triagePatientIds?.length;

  const { getActiveGroup, isLoading: pgLoading } = usePatientGroup();
  /**
   * Use undefined here as a way to differentiate between
   * null, which is the 'All Patients View' and undefined,
   * which is 'not done fetching yet'
   */
  const activeGroup = !pgLoading ? getActiveGroup() : undefined;
  const { name: activeGroupName } = useGetActiveGroupName();
  const urlStateDispatch = useUrlStateUpdater();
  const { pgForm, toggleForm } = useTogglePatientGroupForm();
  const [page, setPage] = useState(1);
  const [tag, setTag] = useState('');
  const [appFilter, setAppFilter] = useState('');
  const [query, setQuery] = useState('');
  const [currentTemplate, setCurrentTemplate] = useState<IPMTemplate>(null);
  const [isSending, setIsSending] = useState(false);
  const { pinnedIds, isPinned, togglePin, isUpdating } = usePinnedTemplates();
  const hasActiveFilters =
    tag?.length > 0 || appFilter?.length > 0 || query?.length > 0;
  const { templates, pinnedTemplates, total } = useGetPMTemplates({
    tag,
    templateType: 'pm',
    app: appFilter,
    body: query,
    page: page - 1,
    limit: TEMPLATE_PAGE_LIMIT,
    pinnedIds: hasActiveFilters ? [] : pinnedIds
  });

  const filter = (tag: string, appFilter: string, query: string) => {
    setTag(tag);
    setAppFilter(appFilter);
    setQuery(query);
    if (tag?.length > 0 || appFilter?.length > 0 || query?.length > 0) {
      setPage(1);
      setCurrentTemplate(null);
    }
  };

  const sendPushMessage = () => {
    if (isSending) {
      return;
    }
    if (!currentTemplate) {
      return;
    }
    // Extract filters from current active patient group
    const filters = {
      contactIds: null,
      tagIds: null,
      apps: null,
      patientIds: triagePatientIds || null
    };
    if (activeGroup && activeGroup.filters) {
      filters.contactIds = activeGroup.filters.contactIds;
      filters.tagIds = activeGroup.filters.tagIds;
      filters.apps = activeGroup.filters.apps;
    }
    const hasNoFilters =
      !filters.contactIds && !filters.tagIds && !filters.apps;
    const allPatientsCallout = hasNoFilters ? (
      <Callout intent="warning" className="mt-3 mb-4">
        You are about to send a push message to{' '}
        <strong>all {terms.patient.plural} in this account.</strong> Please
        double check that this is intended. If it is not, press Cancel and
        choose a different {terms.patient.capitalized} View.
      </Callout>
    ) : null;
    Modal.confirm({
      title: 'Confirm Bulk Push Message Send',
      content: (
        <React.Fragment>
          {!triagePatientIds ? (
            <p>
              You are about to send the following push message to{' '}
              <strong>
                all {terms.patient.plural} in the "{activeGroupName}"{' '}
                {terms.patient.capitalized} View
              </strong>
              . Press Confirm to send, otherwise press Cancel.
            </p>
          ) : (
            <p>
              You are about to send the following push message to{' '}
              <strong>
                all {patientCount} {terms.patient.plural} in the "{patientView}"
                View
              </strong>
              . Press Confirm to send, otherwise press Cancel.
            </p>
          )}
          <Callout intent="primary" className="mt-3 mb-4">
            Sending to a large number of {terms.patient.plural}? Check back in
            30-60 minutes to see the status of the bulk send.
          </Callout>
          {!triagePatientIds ? allPatientsCallout : null}
          <div>
            <PMTemplateCard template={currentTemplate} apps={apps} />
          </div>
        </React.Fragment>
      ),
      onOk: () => {
        setIsSending(true);
        PostJSON<SendBulkPMResponse>({
          url: '/pm/send_bulk/',
          params: {
            templateId: currentTemplate.id,
            filters: filters
          }
        }).then((response) => {
          setIsSending(false);
          if (response.status !== 'OK') {
            Modal.error({
              title: 'Error Sending Push Message',
              content: response.message
            });
            return;
          }
          setCurrentTemplate(null);
          Toast({
            message: !triagePatientIds
              ? `Push message sent to all ${terms.patient.plural} in the ${activeGroupName} Patient View`
              : `Push message sent to all ${patientCount} ${terms.patient.plural} in the ${patientView} View`,
            intent: 'success'
          });
        });
      }
    });
  };

  /**
   * PatientProvider relies on the URL State context's
   * isOpenViaUrl state, which defaults to true, so
   * we need to set it to false here
   */
  useEffect(() => {
    urlStateDispatch({
      type: UrlStateActionType.SetIsOpenViaUrl,
      payload: false
    });
  }, []);

  return (
    <React.Fragment>
      {pgForm.isOpen && (
        <PatientGroupForm isNew={pgForm.isNew} toggleForm={toggleForm} />
      )}
      <Container>
        <Row className="mb-6">
          {!triagePatientIds ? (
            <Col xs={8}>
              <Label className="mb-0-important">Currently Filtering By:</Label>
              <PatientGroupControl
                wrapperClassName="ma-0 font-bold"
                toggleForm={toggleForm}
                setIsPGSelectExpanded={setIsPGSelectExpanded}
              >
                <Button
                  variant="minimal"
                  bare
                  size="large"
                  className="mr-2"
                  aria-label={`Select a Different ${terms.patient.capitalized} View`}
                  aria-expanded={isPGSelectExpanded}
                >
                  <span
                    className="inline-block align-top cursor-pointer"
                    style={{
                      width: '220px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    <AutoText ellipsize>{activeGroupName}</AutoText>
                  </span>
                  <Icon className="ml-2" icon={faCaretDown} />
                </Button>
              </PatientGroupControl>
            </Col>
          ) : null}
          <Col xs={!triageCategory ? 16 : 24}>
            <Button
              className="block"
              size="large"
              fill
              intent="primary"
              onClick={sendPushMessage}
              disabled={isSending || !currentTemplate}
            >
              <Icon icon={faShare} className="mr-2" /> Send Selected Push
              Message
            </Button>
          </Col>
        </Row>
        <Row className="mb-6">
          <Col>
            <CardFrame>
              <CardBlock>
                <div className="mb-6">
                  <PushMessageFilter
                    filter={filter}
                    filterBtnMargin="35px"
                    showChatTemplateFilter={false}
                  />
                </div>
                <div>
                  {!hasActiveFilters &&
                    pinnedTemplates &&
                    pinnedTemplates.length > 0 && (
                      <>
                        <div className="mb-3 mt-1">
                          <b className="text-muted">Pinned</b>
                        </div>
                        {pinnedTemplates.map((template) => (
                          <PMTemplateCard
                            key={`pinned-${template.id}`}
                            template={template}
                            apps={apps}
                            onClick={(template) => setCurrentTemplate(template)}
                            isSelected={
                              currentTemplate &&
                              template.id === currentTemplate.id
                            }
                            isPinned={true}
                            isPinUpdating={isUpdating}
                            onTogglePin={(template) => togglePin(template.id)}
                          />
                        ))}
                        <div className="mb-3 mt-4">
                          <b className="text-muted">All Templates</b>
                        </div>
                      </>
                    )}
                  {templates &&
                    templates.map((template) => (
                      <PMTemplateCard
                        key={template.id}
                        template={template}
                        apps={apps}
                        onClick={(template) => setCurrentTemplate(template)}
                        isSelected={
                          currentTemplate && template.id === currentTemplate.id
                        }
                        isPinned={isPinned(template.id)}
                        isPinUpdating={isUpdating}
                        onTogglePin={(template) => togglePin(template.id)}
                      />
                    ))}
                  {templates &&
                    templates.length === 0 &&
                    (!pinnedTemplates || pinnedTemplates.length === 0) && (
                      <span>No matching templates.</span>
                    )}
                </div>
                <Pagination
                  page={page}
                  total={total}
                  limit={TEMPLATE_PAGE_LIMIT}
                  onPageChange={setPage}
                />
              </CardBlock>
            </CardFrame>
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default NewBulkPM;
